import _ from 'lodash';
import { useState, useEffect, useRef } from 'react';
import { createTimerForApi } from 'src/common/api';
import alphaRealEstate from 'src/request/alphaRealEstate';
import { AxiosInstance } from 'axios';

interface MethodType {
  [key: string]: any;
}
interface Options {
  leading?: boolean; // 是否首次请求
  needQuery?: boolean; // 是否需要在url 后添加参数 ?xxx=xxx
}
const SITE_NAME = process.env.REACT_APP_SITE_NAME;
const AXIOS_MAP: Record<string, AxiosInstance> = {
  'alpha-real-estate': alphaRealEstate,
};
const axios = AXIOS_MAP?.[SITE_NAME as string] || alphaRealEstate;

const GET = (url: string, params: any) => axios({ url, method: 'GET', params });
const GET_BLOB = (url: string, params: any) => axios({ url, method: 'GET', params, responseType: 'blob' });
const POST = (url: string, data: any) => axios({ url, method: 'POST', data });
const POST_BLOB = (url: string, data: any) => axios({ url, method: 'POST', data, responseType: 'blob' });
const DELETE = (url: string, params: any) => axios({ url, method: 'DELETE', params });
const PUT = (url: string, params: any) => axios({ url, method: 'PUT', params });
const METHODTYPE: MethodType = {
  get: GET,
  getBlob: GET_BLOB,
  post: POST,
  postBlob: POST_BLOB,
  delete: DELETE,
  put: PUT,
};

const useFetch = (method: string, url: any, params: any, options: Options | undefined) => {
  const [res, setRes] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetch = METHODTYPE[method];
  const prevParams = useRef(params);

  const fetchData = async (newParams?: any) => {
    setLoading(true);
    setError(null);
    if (options?.needQuery) {
      const allSearchParamsStr = new URLSearchParams(newParams || params).toString();
      url = `${url}?${allSearchParamsStr}`;
    }
    try {
      const response = await createTimerForApi(fetch)(url, newParams || params);

      setRes(response);
      return await Promise.resolve(response);
    } catch (err: any) {
      setError(err);
      setRes(null);
      return await Promise.reject(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (options?.leading) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (!_.isEqual(prevParams.current, params)) {
      fetchData(); // 仅在 params 发生实际变化时调用 fetchData
      prevParams.current = params; // 更新 prevParams 的值
    }
  }, [params]);

  return {
    data: res?.result || res?.data || null,
    res,
    loading,
    error,
    fetchData,
    setData: setRes,
    apiTimer: res?.api_timer,
  };
};

const useGet = (url: string, params?: any, options?: Options) => useFetch('get', url, params, options);
const useGetBlob = (url: string, params?: any, options?: Options) => useFetch('getBlob', url, params, options);
const usePost = (url: string, data?: any, options?: Options) => useFetch('post', url, data, options);
const usePostBlob = (url: string, data?: any, options?: Options) => useFetch('postBlob', url, data, options);
const useDelete = (url: string, params?: any, options?: Options) => useFetch('delete', url, params, options);
const usePut = (url: string, params?: any, options?: Options) => useFetch('put', url, params, options);

export {
  useGet, useGetBlob, usePost, usePostBlob, useDelete, usePut,
};
